import type { ReactNode } from "react";
import Link from "next/link";
import SectionHeader from "@/components/landing/section-header";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const linkClass =
  "underline underline-offset-4 hover:text-foreground";

const faqItems: { question: string; answer: ReactNode }[] = [
  {
    question: "Why is Photon faster?",
    answer: (
      <>
        Photon is built from the ground up for Moondream. We wrote custom CUDA
        kernels tuned to Moondream&apos;s architecture, an optimized scheduler that
        eliminates GPU bubbles, and native code for image encoding, decoding,
        and resizing. Generic inference stacks can&apos;t make these optimizations
        because they have to support arbitrary models.
      </>
    ),
  },
  {
    question: "How does Photon compare to other inference options?",
    answer:
      "Compared to Hugging Face Transformers, Photon delivers roughly 5x higher throughput and 57% lower latency. Compared to vLLM running a similar-sized model, Photon is about 2x faster. Photon also has native support for Moondream fine-tunes, which generic inference stacks don't offer.",
  },
  {
    question: "Where can I run Photon?",
    answer: (
      <>
        Photon runs on NVIDIA GPUs from Ampere through Blackwell, including
        datacenter GPUs like H100 and A100, desktop GPUs like RTX 4090, and
        edge devices like Jetson AGX Orin. For deployments on devices with less
        than 8 GB of RAM,{" "}
        <a href="mailto:sales@moondream.ai" className={linkClass}>
          contact us
        </a>
        .
      </>
    ),
  },
  {
    question: "What does it cost?",
    answer: (
      <>
        Photon uses pay-as-you-go pricing with low per-token rates, so you only
        pay for the inference you actually run. Every account includes $5 in
        free usage each month, enough for approximately 30,000 requests. See{" "}
        <Link href="/pricing" className={linkClass}>
          pricing
        </Link>{" "}
        for details. If you need dedicated capacity, larger commitments, or
        enterprise pricing,{" "}
        <a href="mailto:sales@moondream.ai" className={linkClass}>
          email us
        </a>{" "}
        or{" "}
        <a
          href="https://cal.com/moondream"
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          book a call
        </a>
        .
      </>
    ),
  },
  {
    question: "Does Photon track my usage?",
    answer:
      "Photon collects high-level usage information for billing. It never collects your images, prompts, or data. All inference runs entirely on your hardware.",
  },
  {
    question: "Will Photon work with my Moondream fine-tunes?",
    answer:
      "Yes. Photon has native support for Moondream fine-tunes and can load them directly by fine-tune ID.",
  },
  {
    question: "Can I use Photon with NVIDIA Triton Inference Server?",
    answer: (
      <>
        Yes. We provide a{" "}
        <a
          href="https://github.com/m87-labs/kestrel/tree/main/triton"
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          Triton backend for Photon
        </a>{" "}
        so you can integrate it into existing Triton deployments.
      </>
    ),
  },
  {
    question: "How do I get help?",
    answer: (
      <>
        Free community support is available on our{" "}
        <a
          href="https://discord.com/invite/tRUdpjDQfH"
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          Discord
        </a>
        . For dedicated support with guaranteed response times, see our{" "}
        <Link href="/pricing" className={linkClass}>
          support plans
        </Link>
        .
      </>
    ),
  },
];

export function FAQAccordion({ hideHeader = false }: { hideHeader?: boolean }) {
  return (
    <section className="scroll-mt-24 space-y-10" id="faq">
      {hideHeader ? null : (
        <SectionHeader
          section="FAQ"
          heading="Frequently asked questions"
          description="Concise answers for teams evaluating Photon for production inference."
        />
      )}

      <Accordion type="single" collapsible className="w-full">
        {faqItems.map((item) => (
          <AccordionItem key={item.question} value={item.question}>
            <AccordionTrigger className="text-left text-base font-medium hover:no-underline">
              {item.question}
            </AccordionTrigger>
            <AccordionContent>
              <div className="max-w-3xl text-sm leading-6 text-muted-foreground">
                {item.answer}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
